import { YeshumanMarketingChrome } from '@/components/landing/yeshuman/yeshuman-marketing-chrome'
import {
  yeshumanAnn,
  yeshumanAnnDim,
  yeshumanDisplayHeading,
  yeshumanEditorial,
  yeshumanEditorialSm,
  yeshumanMeasure,
} from '@/components/landing/yeshuman/yeshuman-typography'
import {
  yeshumanBand,
  yeshumanBandAlt,
  yeshumanContentColumn,
} from '@/components/landing/yeshuman/yeshuman-layout'
import { useGuestSurfaceTheme } from '@/hooks/use-guest-surface-theme'
import { LandingCtaLink } from '@/landing/landing-cta-link'
import { cn } from '@/lib/utils'

const sectionPad = 'py-[clamp(96px,9vw,132px)]'

const EXCALIDRAW_BOARD_URL = 'https://link.excalidraw.com/readonly/D17mazceWHXvVWQ53eb5'

function excalidrawEmbedSrc(theme: 'light' | 'dark') {
  const url = new URL(EXCALIDRAW_BOARD_URL)
  url.searchParams.set('darkMode', String(theme === 'dark'))
  return url.toString()
}

export function YeshumanConceptsTopologyPage() {
  const { guestTheme } = useGuestSurfaceTheme()

  return (
    <YeshumanMarketingChrome stickyHeaderContentInset>
      <main id="main" tabIndex={-1} className="outline-none">
        <section
          aria-labelledby="yeshuman-topology-heading"
          className={cn(sectionPad, yeshumanBand)}
        >
          <div className={yeshumanContentColumn}>
            <div className={cn('mb-7', yeshumanAnn, yeshumanAnnDim)}>Topology</div>
            <h1 id="yeshuman-topology-heading" className={cn(yeshumanDisplayHeading, 'max-w-[16em]')}>
              The system diagram.
            </h1>
            <p className={cn(yeshumanEditorial, yeshumanMeasure, 'mt-7')}>
              How the pieces connect. You can pan and zoom. The legend below is what we mean by the
              words, including what is shipped and what is further work.
            </p>
          </div>
        </section>

        <section className={cn('pb-[clamp(96px,9vw,132px)]', yeshumanBandAlt)} aria-label="Topology">
          <div className={yeshumanContentColumn}>
            <div className="relative hidden aspect-[16/10] w-full overflow-hidden border border-[var(--marketing-border)] md:block">
              <iframe
                key={`desktop-${guestTheme}`}
                src={excalidrawEmbedSrc(guestTheme)}
                title="Yes Human topology"
                loading="lazy"
                referrerPolicy="no-referrer"
                className="absolute inset-0 h-full w-full border-0"
              />
            </div>
            <div className="relative aspect-[4/5] w-full overflow-hidden border border-[var(--marketing-border)] md:hidden">
              <iframe
                key={`mobile-${guestTheme}`}
                src={excalidrawEmbedSrc(guestTheme)}
                title="Yes Human topology"
                loading="lazy"
                referrerPolicy="no-referrer"
                allow="fullscreen"
                className="absolute inset-0 h-full w-full border-0 touch-pan-y"
              />
            </div>
            <div className={cn(yeshumanEditorialSm, yeshumanMeasure, 'mt-8')}>
              <p className={cn('mb-3', yeshumanAnn, yeshumanAnnDim)}>Legend</p>
              <ul className="flex list-none flex-col gap-3">
                <li>
                  <span className="text-[var(--marketing-text)]">Harness.</span> One LangGraph
                  runtime: tool calling, memory, the agent loop. The stack has several.
                </li>
                <li>
                  <span className="text-[var(--marketing-text)]">Connectors.</span> Usually MCP. Also
                  APIs, client adapters, and CLIs on sandboxes.
                </li>
                <li>
                  <span className="text-[var(--marketing-text)]">Memory.</span> Agent memory about a
                  user. What people tell it stays, so later work can use it.
                </li>
                <li>
                  <span className="text-[var(--marketing-text)]">Surface.</span> UIs, voice, phone,
                  iOS.
                </li>
                <li>
                  <span className="text-[var(--marketing-text)]">Further work.</span> Chat and voice
                  are live. An Intelligence Graph is in development. Other tools plug in as they
                  ship.
                </li>
              </ul>
            </div>
            <p className={cn('mt-10', yeshumanEditorialSm, yeshumanAnnDim)}>
              <LandingCtaLink to="/concepts" className={yeshumanAnn}>
                What these words mean
              </LandingCtaLink>
            </p>
            <p className={cn('mt-4', yeshumanEditorialSm, yeshumanAnnDim)}>
              <LandingCtaLink to="/" className={yeshumanAnn}>
                Back to Yes Human
              </LandingCtaLink>
            </p>
          </div>
        </section>
      </main>
    </YeshumanMarketingChrome>
  )
}
